"use client";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useShallow } from "zustand/react/shallow";
import { useAuth } from "@/components/providers/auth-context";
import { ResponsiveModal } from "@/components/responsive-modal";
import { useI18n } from "@/locales/client";
import { useTRPC } from "@/trpc/client";
import useCancellationModal from "../../hooks/use-cancellation-modal";
import { useApplyRetentionOffer } from "../api/use-apply-retention-offer";
import { useCancelSubscription } from "../api/use-cancel-subscription";
import { usePauseSubscription } from "../api/use-pause-subscription";
import { AlternativeFeedbackStep } from "./alternative-feedback-step";
import { ComparisonStep } from "./comparison-step";
import { FeedbackStep } from "./feedback-step";
import { NoLongerNeedReasonStep } from "./no-longer-need-reason-step";
import { OfferStep } from "./offer-step";
import { OtherReasonFeedbackStep } from "./other-reason-feedback-step";
import { PauseTimeframeStep } from "./pause-timeframe-step";
import { ReasonStep } from "./reason-step";
import { ThankYouStep } from "./thank-you-step";

export const CancellationModal = () => {
  const {
    isOpen,
    step,
    selectedReason,
    noLongerNeedReason,
    feedbackSubmitted,
    feedbackText,
    feedbackRating,
    setStep,
    setReason,
    setNoLongerNeedReason,
    setFeedbackSubmitted,
    onClose,
  } = useCancellationModal(
    useShallow((state) => ({
      isOpen: state.isOpen,
      step: state.step,
      selectedReason: state.selectedReason,
      noLongerNeedReason: state.noLongerNeedReason,
      feedbackSubmitted: state.feedbackSubmitted,
      feedbackText: state.feedbackText,
      feedbackRating: state.feedbackRating,
      setStep: state.setStep,
      setReason: state.setReason,
      setNoLongerNeedReason: state.setNoLongerNeedReason,
      setFeedbackSubmitted: state.setFeedbackSubmitted,
      onClose: state.onClose,
    })),
  );

  const t = useI18n();
  const trpc = useTRPC();
  const { session } = useAuth();
  const firstName = session?.user.user_metadata?.name?.split(" ")[0] ?? "";
  const { data: userPlan } = useQuery(
    trpc.users.getUserPlan.queryOptions(undefined, {
      enabled: isOpen,
    }),
  );
  const hasDiscount = userPlan?.hasDiscount ?? false;
  const hasUsedRetentionOffer = userPlan?.hasUsedRetentionOffer ?? false;
  const planAmount = userPlan?.planAmount ?? null;
  const planInterval = userPlan?.planInterval ?? null;

  const { mutate: cancelSubscription, isPending: isCanceling } =
    useCancelSubscription();
  const { mutate: applyRetentionOffer, isPending: isClaimingOffer } =
    useApplyRetentionOffer();
  const { mutate: pauseSubscription, isPending: isPausing } =
    usePauseSubscription();
  const {
    mutate: submitCancellationFeedback,
    isPending: isSubmittingFeedback,
  } = useMutation(
    trpc.cancellation.submitCancellationFeedback.mutationOptions(),
  );

  const isDiscountReason =
    selectedReason === "too_expensive" || !selectedReason;

  const effectiveReason =
    isDiscountReason && hasDiscount
      ? "discount_reminder"
      : isDiscountReason && hasUsedRetentionOffer
        ? "no_longer_need"
        : (selectedReason ?? "other");

  const getTitle = () => {
    switch (step) {
      case "comparison":
        return t("cancellation.title.comparison");
      case "reason":
        // @ts-expect-error
        return t("cancellation.title.reason", { name: firstName });
      case "offer":
        return getOfferTitle();
      case "feedback":
        return t("cancellation.title.feedback");
      case "thank_you":
        return t("cancellation.title.thankYou");
      case "alternative_feedback":
        return t("cancellation.title.alternativeFeedback");
      case "no_longer_need_reason":
        return t("cancellation.noLongerNeed.title");
      case "pause_timeframe":
        return t("cancellation.pauseTimeframe.title");
      case "other_reason_feedback":
        return t("cancellation.otherReasonFeedback.title");
    }
  };

  const getOfferTitle = () => {
    switch (effectiveReason) {
      case "too_expensive":
      case "other":
        return t("cancellation.title.offerDiscount");
      case "discount_reminder":
        return t("cancellation.title.offerDiscountReminder");
      case "no_longer_need":
        return t("cancellation.title.offerPause");
      case "need_more_features":
        return t("cancellation.title.offerFeatures");
      case "quality_less_than_expected":
        return t("cancellation.title.offerQuality");
      default:
        return t("cancellation.title.offerDiscount");
    }
  };

  const getDescription = () => {
    switch (step) {
      case "comparison":
        return t("cancellation.description.comparison");
      default:
        return undefined;
    }
  };

  const doCancelSubscription = () => {
    const reason = noLongerNeedReason
      ? `${selectedReason}:${noLongerNeedReason}`
      : (selectedReason ?? undefined);
    cancelSubscription({ reason }, { onSuccess: () => onClose() });
  };

  const handleCancel = () => {
    if (!feedbackSubmitted && selectedReason) {
      submitCancellationFeedback({
        reason: selectedReason,
        outcome: "canceled",
        noLongerNeedReason: noLongerNeedReason ?? undefined,
      });
    }
    doCancelSubscription();
  };

  const submitRetainedFeedback = () => {
    if (selectedReason) {
      submitCancellationFeedback({
        reason: selectedReason,
        outcome: "retained",
        noLongerNeedReason: noLongerNeedReason ?? undefined,
      });
    }
  };

  const handleClaimOffer = () => {
    switch (effectiveReason) {
      case "too_expensive":
      case "other":
        submitRetainedFeedback();
        applyRetentionOffer(undefined, { onSuccess: () => onClose() });
        break;
      case "discount_reminder":
        submitRetainedFeedback();
        onClose();
        break;
      case "no_longer_need":
        if (selectedReason) {
          submitCancellationFeedback({
            reason: selectedReason,
            outcome: "paused",
            noLongerNeedReason: noLongerNeedReason ?? undefined,
          });
        }
        pauseSubscription(undefined, { onSuccess: () => onClose() });
        break;
      case "need_more_features":
      case "quality_less_than_expected":
        setStep("feedback");
        break;
      default:
        submitRetainedFeedback();
        applyRetentionOffer(undefined, { onSuccess: () => onClose() });
        break;
    }
  };

  const isOfferPending = isClaimingOffer || isPausing;

  const renderContent = () => {
    switch (step) {
      case "comparison":
        return (
          <ComparisonStep onKeep={onClose} onCancel={() => setStep("reason")} />
        );
      case "reason":
        return (
          <ReasonStep
            selectedReason={selectedReason}
            onSelectReason={setReason}
            onBack={() => setStep("comparison")}
            onContinue={() => {
              if (selectedReason === "no_longer_need") {
                setStep("no_longer_need_reason");
              } else if (selectedReason === "other") {
                setStep("other_reason_feedback");
              } else if (selectedReason === "found_alternative") {
                setStep("alternative_feedback");
              } else {
                setStep("offer");
              }
            }}
          />
        );
      case "offer":
        return (
          <OfferStep
            reason={effectiveReason}
            onClaimOffer={handleClaimOffer}
            onContinueCanceling={handleCancel}
            isClaimingOffer={isOfferPending}
            isCanceling={isCanceling}
            planAmount={planAmount}
            planInterval={planInterval}
          />
        );
      case "feedback":
        return (
          <FeedbackStep
            reason={selectedReason ?? "other"}
            onBack={() => setStep("offer")}
            onSuccess={(text, rating) => {
              setFeedbackSubmitted(text, rating);
              setStep("thank_you");
            }}
          />
        );
      case "alternative_feedback":
        return (
          <AlternativeFeedbackStep
            onBack={() => setStep("reason")}
            onFeedbackSubmitted={setFeedbackSubmitted}
            onSubmit={doCancelSubscription}
            isSubmitting={isCanceling}
          />
        );
      case "no_longer_need_reason":
        return (
          <NoLongerNeedReasonStep
            selectedSubReason={noLongerNeedReason}
            onSelectSubReason={setNoLongerNeedReason}
            onBack={() => setStep("reason")}
            isLoading={isSubmittingFeedback}
            onContinue={() => {
              if (
                noLongerNeedReason === "exam_over" ||
                noLongerNeedReason === "taking_break"
              ) {
                setStep("pause_timeframe");
              } else if (noLongerNeedReason === "graduated") {
                submitCancellationFeedback({
                  reason: "no_longer_need",
                  outcome: "canceled",
                  noLongerNeedReason: "graduated",
                });
                cancelSubscription(
                  { reason: "no_longer_need:graduated" },
                  { onSuccess: () => onClose() },
                );
              } else {
                setStep("other_reason_feedback");
              }
            }}
          />
        );
      case "other_reason_feedback":
        return (
          <OtherReasonFeedbackStep
            reason={selectedReason ?? "other"}
            noLongerNeedReason={noLongerNeedReason ?? undefined}
            onBack={() =>
              setStep(
                selectedReason === "other" ? "reason" : "no_longer_need_reason",
              )
            }
            onFeedbackSubmitted={setFeedbackSubmitted}
            onSubmit={doCancelSubscription}
            isSubmitting={isCanceling}
          />
        );
      case "pause_timeframe":
        return (
          <PauseTimeframeStep
            onPause={(timeframe) => {
              submitCancellationFeedback({
                reason: selectedReason ?? "no_longer_need",
                outcome: "paused",
                noLongerNeedReason: noLongerNeedReason ?? undefined,
              });
              pauseSubscription(
                {
                  timeframe: timeframe as
                    | "2_weeks"
                    | "1_month"
                    | "2_months"
                    | "next_semester"
                    | "not_sure",
                },
                { onSuccess: () => onClose() },
              );
            }}
            onContinueCanceling={handleCancel}
            isPausing={isPausing}
            isCanceling={isCanceling}
          />
        );
      case "thank_you":
        return (
          <ThankYouStep
            firstName={firstName}
            onStay={onClose}
            onContinueCanceling={() => {
              submitCancellationFeedback({
                reason: selectedReason ?? "other",
                outcome: "canceled",
                noLongerNeedReason: noLongerNeedReason ?? undefined,
                feedbackText: feedbackText ?? undefined,
                rating: feedbackRating ?? undefined,
              });
              doCancelSubscription();
            }}
            isCanceling={isCanceling}
          />
        );
    }
  };

  return (
    <ResponsiveModal
      title={getTitle()}
      titleClassName="text-2xl"
      description={getDescription()}
      form={renderContent()}
      open={isOpen}
      onOpenChange={onClose}
    />
  );
};
